import React, { Component } from 'react';
import PropTypes from 'prop-types';

import OutlinedTextField from '../textFields/OutlinedTextField';
import SectionDivider from '../../shared/SectionDivider';
import AltLoginsForm from '../AltLoginsForm';
import Wrapper from '../Wrapper';
import FormButton from '../FormButton';

import baseStyles from '../../../styles/baseStyles.module.scss';
import formStyles from '../styles/styles.module.scss';
import buttonStyles from '../buttons/styles/styles.module.scss';
import styles from './styles/styles.module.scss';

import { validateUserForm } from '../../../utils/form_validaters/user_form_helper';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { loadRecaptcha, executeRecaptcha } from '../../../utils/recaptcha_helpers';
import { facebookHandler, appleHandler, googleHandler } from '../../../utils/oauth_handlers';
import { ProPageStrings, StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../utils/language_support_util';

const { OR } = StringConstantsMixin.strings;
const {
  loginText: { forgotPassword }
} = ProPageStrings;

class LoginForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      userEmail: '',
      userPassword: '',
      errors: {
        userEmail: '',
        userPassword: ''
      },
      serverError: null,
      submitting: false,
      disableSubmit: this.props.enableRecaptcha,
      recaptchaObject: null,
      showErrorsOnType: false
    };
  }

  static LOGIN_URL = '/api/alltrails/login';

  static FORGOT_PASSWORD_URL = '/users/password/new';

  static SIGN_UP_PAGE_URL = '/signup';

  static TEXT = { ...ProPageStrings.loginText, ...ProPageStrings.loginForm, ...ProPageStrings.formText, ...ProPageStrings.altLoginsText };

  componentDidMount() {
    if (this.props.enableRecaptcha) {
      loadRecaptcha(recaptchaObject => {
        this.setState({ disableSubmit: false, recaptchaObject });
      });
    }
  }

  // validations

  failedFormValidation() {
    let validationErrors = { ...this.state.errors };
    let validation;
    for (const name in this.state.errors) {
      validation = validateUserForm(name, this.state[name], validationErrors);
      validationErrors = { ...validationErrors, ...validation.errors };
    }

    this.setState({ errors: validationErrors });

    return Object.keys(validationErrors).some(error => validationErrors[error].length > 0);
  }

  loginSuccessCallback = data => {
    if (this.props.handleLoginSuccess) {
      return this.props.handleLoginSuccess(data);
    }

    window.location.href = data.welcome_notice
      ? `${this.props.returnTo + (this.props.returnTo.includes('?') ? '&' : '?')}welcome=${data.welcome_notice}`
      : this.props.returnTo;
  };

  loginErrorCallback = err => {
    if (err && err.responseJSON && err.responseJSON.errors) {
      this.setState({ serverError: err.responseJSON.errors[0].message, submitting: false });
    } else {
      this.setState({ serverError: StringConstantsMixin.strings.ERR_TRY_AGAIN, submitting: false });
    }
  };

  loginCompleteCallback = () => {};

  submitLogin = recaptchaToken => {
    const headers = this.props.locale ? { 'X-Language-Locale': this.props.locale } : {};
    const params = {
      email: this.state.userEmail,
      password: this.state.userPassword,
      create_session: true,
      recaptcha: recaptchaToken
    };

    ServerCommunicationUtil.postApiEndpoint(
      LoginForm.LOGIN_URL,
      params,
      this.loginSuccessCallback,
      this.loginErrorCallback,
      this.loginCompleteCallback,
      headers
    );
  };

  handleLogin = e => {
    e.preventDefault();

    if (this.state.disableSubmit || this.state.submitting) {
      return;
    }

    if (this.failedFormValidation()) {
      this.setState({ showErrorsOnType: true });
      return;
    }

    this.setState({ submitting: true });
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (this.props.enableRecaptcha) {
      executeRecaptcha(this.state.recaptchaObject, 'userSignupLogin').then(
        recaptchaToken => this.submitLogin(recaptchaToken),
        () => this.setState({ submitting: false })
      );
    } else {
      this.submitLogin();
    }
  };

  handleChange = e => {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (this.state.showErrorsOnType) {
      const errors = validateUserForm(name, value, { ...this.state.errors });
      this.setState({ ...errors, [name]: value });
    } else {
      this.setState({ [name]: value });
    }
  };

  handleGoogle = response => {
    googleHandler(response, this.props.locale, this.loginSuccessCallback, () => {}, this.loginCompleteCallback);
  };

  handleFacebook = response => {
    facebookHandler(response, this.props.locale, this.loginSuccessCallback, () => {}, this.loginCompleteCallback);
  };

  handleSignUpClick = () => {
    if (this.props.handleRegisterClick) {
      return this.props.handleRegisterClick();
    }

    window.location.href = LanguageSupportUtil.wrapUrlSafe(LoginForm.SIGN_UP_PAGE_URL, this.props.locale);
  };

  renderEmailTextInput() {
    return (
      <div className={`${formStyles.inputItem}`}>
        <OutlinedTextField
          name="userEmail"
          isError={this.state.errors.userEmail.length > 0}
          errorMessage={this.state.errors.userEmail}
          value={this.state.userEmail}
          label={LoginForm.TEXT.email.title}
          changeHandler={this.handleChange}
          enterKeyHandler={this.handleLogin}
          type="email"
        />
      </div>
    );
  }

  renderPasswordInput() {
    return (
      <div className={`${formStyles.inputItem}`}>
        <OutlinedTextField
          name="userPassword"
          isError={this.state.errors.userPassword.length > 0}
          errorMessage={this.state.errors.userPassword}
          value={this.state.userPassword}
          label={LoginForm.TEXT.password.title}
          changeHandler={this.handleChange}
          enterKeyHandler={this.handleLogin}
          type="password"
        />
      </div>
    );
  }

  renderServerError() {
    if (!this.state.serverError) {
      return null;
    }

    return <div className={formStyles.serverError}>{this.state.serverError}</div>;
  }

  renderSubmitBtn() {
    const disabled = this.state.disableSubmit || this.state.submitting;
    return <FormButton value={LoginForm.TEXT.login} disabled={disabled} />;
  }

  renderForm() {
    return (
      <form onSubmit={this.handleLogin}>
        {this.renderEmailTextInput()}
        {this.renderPasswordInput()}
        {this.renderServerError()}
        {this.renderSubmitBtn()}
      </form>
    );
  }

  renderFooter() {
    return (
      <>
        <div>
          {LoginForm.TEXT.privacyPolicyLead}{' '}
          <a href="/terms" className={baseStyles.route}>
            {LoginForm.TEXT.termsLink}
          </a>{' '}
          {LoginForm.TEXT.and}{' '}
          <a href="/privacy" className={baseStyles.route}>
            {LoginForm.TEXT.privacy}
          </a>
          .
        </div>
        <div className={`${baseStyles.action}`}>
          {LoginForm.TEXT.noAccount}{' '}
          <span className={`${baseStyles.route} ${baseStyles.route}`} onClick={this.handleSignUpClick}>
            {LoginForm.TEXT.signUpFree}
          </span>
        </div>
      </>
    );
  }

  render() {
    const foot = this.renderFooter();

    return (
      <Wrapper text={LoginForm.TEXT} foot={foot}>
        <div className={baseStyles.section}>
          {this.renderForm()}
          <div className={`${formStyles.action} ${styles.forgotPassword}`}>
            <a className={`${buttonStyles.text}`} href={LanguageSupportUtil.wrapUrlSafe(LoginForm.FORGOT_PASSWORD_URL, this.props.locale)}>
              {forgotPassword}
            </a>
          </div>
        </div>
        <SectionDivider classes={styles.sectionDivider}>{OR}</SectionDivider>
        <AltLoginsForm
          facebookAppId={this.props.facebookAppId}
          googleClientId={this.props.googleClientId}
          googleLoginHandler={this.handleGoogle}
          facebookLoginHandler={this.handleFacebook}
          appleLoginHandler={appleHandler}
        />
      </Wrapper>
    );
  }
}

LoginForm.propTypes = {
  enableRecaptcha: PropTypes.bool.isRequired,
  facebookAppId: PropTypes.number.isRequired,
  googleClientId: PropTypes.string.isRequired,
  handleLoginSuccess: PropTypes.func,
  handleRegisterClick: PropTypes.func,
  locale: PropTypes.string.isRequired,
  returnTo: PropTypes.string // url
};

LoginForm.defaultProps = {
  handleLoginSuccess: null,
  handleRegisterClick: null,
  returnTo: '/'
};

export default LoginForm;
